
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";

interface CaseStudyResult {
  text: string;
}

interface CaseStudyContentProps {
  industry: string;
  companyType?: string;
  title: string;
  challenge: string;
  results: CaseStudyResult[];
  tags?: string[];
  cta?: {
    text: string;
    link: string;
  };
  redactedName?: string;
}

export const CaseStudyContent = ({
  industry,
  companyType,
  title,
  challenge,
  results,
  tags,
  cta,
  redactedName
}: CaseStudyContentProps) => {
  return (
    <div className="col-span-2 p-6">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-medium text-gray-600">{industry}</span>
            {companyType && (
              <>
                <span className="text-gray-300">•</span>
                <span className="text-sm text-gray-600">{companyType}</span>
              </>
            )}
          </div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        
        {/* Challenge */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-1">The Challenge</h4>
          <p className="text-gray-600">
            {challenge}
          </p>
          
          {/* Redacted Company Name */}
          {redactedName && (
            <div className="mt-2">
              <RedactedCompanyName companyName={redactedName} />
            </div>
          )}
        </div>
        
        {/* Results */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Results</h4>
          <ul className="space-y-2">
            {results.map((result, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-flex items-center justify-center flex-shrink-0 w-5 h-5 mr-2 mt-0.5 bg-green-100 text-green-600 rounded-full">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-gray-600">{result.text}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag, index) => (
              <span 
                key={index} 
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* CTA Button - Make sure it's properly linked */}
        {cta && (
          <div className="mt-auto pt-4">
            <Link to={cta.link} className="inline-block">
              <Button 
                variant="outline" 
                className="text-edgefi-purple border-edgefi-purple hover:bg-edgefi-purple/10 group"
              >
                {cta.text}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
